import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {apiUrl, gradeApi} from '../../api.js';
import {PDFView} from '../Teacher/PDFRendered.js';
import SUnpublishedFlexibleAssignment from '../Student/SUnpublishedFlexibleAssignment/SUnpublishedFlexibleAssignment';
import {Check, UserCircleIcon, ChevronDownIcon, ChevronUpIcon, LightbulbIcon} from 'lucide-react';

// Truncated Text component for expandable text display
const TruncatedText = ({text, maxLength = 70, className = ''}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) {
        return null;
    }

    const needsTruncation = text.length > maxLength;
    const displayText = isExpanded || !needsTruncation ? text : `${text.substring(0, maxLength)}...`;

    return (
        <div className={`${className}`}>
            <div className="whitespace-pre-wrap">{displayText}</div>
            {needsTruncation && (
                <button
                    className="mt-1 text-[#e4b17a] hover:text-[#c99b69] text-sm font-medium flex items-center transition-all duration-200"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? (
                        <>
                            <span>Show less</span>
                            <ChevronUpIcon className="w-4 h-4 ml-1"/>
                        </>
                    ) : (
                        <>
                            <span>Show more</span>
                            <ChevronDownIcon className="w-4 h-4 ml-1"/>
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

function PublicGradeView() {
    const {token} = useParams();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignment, setAssignment] = useState({});
    const [submissionData, setSubmissionData] = useState({});
    const [feedbackItems, setFeedbackItems] = useState({});
    const [originalRegradeTexts, setOriginalRegradeTexts] = useState({});
    const [displaySettings, setDisplaySettings] = useState({
        show_pdf: false,
        show_grades: false,
        show_feedback: false
    });

    useEffect(() => {
        getSubmissionFromToken();
    }, [token]);

    const getSubmissionFromToken = async () => {
        try {
            setLoading(true);
            const response = await gradeApi.get({
                path: `/api/grade-access/${token}/`,
                config: {}
            });

            const {assignment, submission, settings} = response.data;

            setAssignment(assignment);
            setSubmissionData(submission);
            setFeedbackItems(submission?.feedback || {});
            if (settings) {
                setDisplaySettings(settings);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching submission with token:', error);
            setError('Invalid or expired access link. Please contact your instructor for a new link.');
            setLoading(false);
        }
    };

    const getInstructorFeedback = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].instructor_text;
        }
        return '';
    };

    const hasRubricItem = (subquestionId, rubricId) => {
        if (!feedbackItems) {
            return false;
        }
        if (!subquestionId) {
            return false;
        }
        if (subquestionId in feedbackItems) {
            let rubricItems = feedbackItems[subquestionId]?.rubric_items;
            for (let i = 0; i < rubricItems.length; i++) {
                if (rubricItems[i].id === rubricId) {
                    return true;
                }
            }
        }
        return false;
    };

    const getFeedback = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].feedback;
        }
        return '';
    };

    const handleRegradeReq = subquestionId => {
        setFeedbackItems(prevFeedbackItems => ({
            ...prevFeedbackItems,
            [subquestionId]: {
                ...prevFeedbackItems[subquestionId],
                regrade_request: true,
                regrade_text: prevFeedbackItems[subquestionId]?.regrade_text || '',
                edit_mode: true,
                is_new_request: true // Flag to track if this is a new request vs editing existing
            }
        }));
    };

    const handleRegradeReqSave = async subquestionId => {
        const regradeReason = feedbackItems[subquestionId]?.regrade_text || '';
        const submissionID = submissionData?.id;

        try {
            await gradeApi.post({
                path: `/api/grade-access/${token}/regrade-request/`,
                body: {
                    assignmentId: assignment.id,
                    submissionId: submissionID,
                    subquestionId: subquestionId,
                    regradeRequest: true,
                    regradeText: regradeReason
                },
                config: {}
            });

            setFeedbackItems(prevFeedbackItems => ({
                ...prevFeedbackItems,
                [subquestionId]: {
                    ...prevFeedbackItems[subquestionId],
                    regrade_request: true,
                    regrade_text: regradeReason,
                    edit_mode: false
                }
            }));

            // Show success message
            setMessage('Regrade request submitted successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error updating regrade request:', error);
            setMessage('Error submitting regrade request. Please try again.');
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleCancelEdit = (subquestionId) => {
        setFeedbackItems(prevFeedbackItems => ({
            ...prevFeedbackItems,
            [subquestionId]: {
                ...prevFeedbackItems[subquestionId],
                edit_mode: false,
                // Restore the text from our saved original
                regrade_text: originalRegradeTexts[subquestionId] || prevFeedbackItems[subquestionId]?.regrade_text || ''
            }
        }));
    };

    const handleRegradeReqCancel = async subquestionId => {
        const submissionID = submissionData?.id;
        const isNewRequest = feedbackItems[subquestionId]?.is_new_request;

        if (isNewRequest) {
            // For new requests that haven't been submitted yet, just reset UI state
            setFeedbackItems(prevFeedbackItems => ({
                ...prevFeedbackItems,
                [subquestionId]: {
                    ...prevFeedbackItems[subquestionId],
                    regrade_request: false,
                    regrade_text: '',
                    edit_mode: false
                }
            }));
        } else {
            // For existing regrade requests, we need to call the API to cancel the request
            try {
                await gradeApi.post({
                    path: `/api/grade-access/${token}/regrade-request/`,
                    body: {
                        assignmentId: assignment.id,
                        submissionId: submissionID,
                        subquestionId: subquestionId,
                        regradeRequest: false,
                        regradeText: ''
                    },
                    config: {}
                });

                // Update UI state after successful API call
                setFeedbackItems(prevFeedbackItems => ({
                    ...prevFeedbackItems,
                    [subquestionId]: {
                        ...prevFeedbackItems[subquestionId],
                        regrade_request: false,
                        regrade_text: '',
                        edit_mode: false
                    }
                }));

                setMessage('Regrade request canceled successfully!');
                setTimeout(() => setMessage(''), 3000);
            } catch (error) {
                console.error('Error canceling regrade request:', error);
                setMessage('Error canceling regrade request. Please try again.');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    const handleEditRegradeReq = subquestionId => {
        // Save the original text before entering edit mode
        setOriginalRegradeTexts(prev => ({
            ...prev,
            [subquestionId]: feedbackItems[subquestionId]?.regrade_text || ''
        }));

        setFeedbackItems(prevFeedbackItems => ({
            ...prevFeedbackItems,
            [subquestionId]: {
                ...prevFeedbackItems[subquestionId],
                edit_mode: true,
                is_new_request: false // Flag this as editing an existing request
            }
        }));
    };

    const ScoreScale = ({subQuestion}) => {
        const studentFeedback = feedbackItems[subQuestion.id];
        const displayScore = studentFeedback ? parseFloat(studentFeedback.score) : 0;
        const maxScore = subQuestion.max_weight !== undefined ? subQuestion.max_weight : 5;
        const simplifyScale = maxScore > 8;
        const scorePoints = [];
        const increment = simplifyScale ? 1 : 0.5;
        for (let i = 0; i <= maxScore; i += increment) {
            scorePoints.push(i);
        }

        return (
            <div className="mt-3 mb-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                {/* Centered Score Display */}
                <div className="flex flex-col items-center mb-3">
                    <span className="text-xs font-medium text-gray-500 mb-1">Points Awarded</span>
                    <span 
                        className={`text-2xl font-bold px-3 py-1.5 rounded-lg border shadow-md 
                                    ${displayScore === maxScore && maxScore > 0 ? 'text-green-700 bg-green-100 border-green-300' : 'text-[#8c6b4a] bg-[#f9ede2] border-[#e4b17a]/50'}`}
                    >
                        {displayScore} / {maxScore}
                    </span>
                </div>

                {/* Number Line / Slider */}
                <div className="relative w-full h-6 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                    <div 
                        className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out 
                                   ${displayScore === maxScore && maxScore > 0 ? 'bg-green-500' : 'bg-[#e4b17a]'}`}
                        style={{ width: `${(displayScore / maxScore) * 100}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-between px-2">
                        {scorePoints.map((point, index) => (
                            <div key={index} className="relative flex flex-col items-center h-full justify-center">
                                <div 
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ease-in-out 
                                        ${point === displayScore ? 
                                            (displayScore === maxScore && maxScore > 0 ? 'bg-green-600 ring-2 ring-green-500/70 scale-125' : 'bg-[#c99b69] ring-2 ring-[#e4b17a]/70 scale-125') : 
                                            'bg-gray-300'}
                                        ${point < displayScore ? (displayScore === maxScore && maxScore > 0 ? 'bg-green-500/80' :'bg-[#e4b17a]/80') : ''}
                                    `}
                                ></div>
                                {/* Labels for score points */}
                                {(maxScore <= 10 || (maxScore > 10 && point % Math.floor(maxScore / 5) === 0) || point === maxScore || point === 0) && (
                                 <span className="absolute -bottom-5 text-xs text-gray-500 select-none">
                                    {point}
                                </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <div
                    className="bg-red-100 border border-red-400 text-red-700 px-8 py-6 rounded-lg max-w-md text-center">
                    <p className="text-lg font-semibold mb-2">Access Error</p>
                    <p>{error}</p>
                    <p className="mt-4 text-sm">If you believe this is an error, please contact your instructor.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full overflow-x-hidden">
            {/* Header */}
            <div className="flex flex-col fixed w-full pb-1 pt-3 bg-white px-5 border-b border-gray-200 z-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <img src="/assets/logo_icon.png" alt="GradeWiz Logo" className="h-20 mr-3"/>
                        <h1 className="text-2xl font-bold mr-4 text-gray-800">GradeWiz</h1> 
                        <div>
                            <h1 className="text-xl font-semibold text-[#8c6b4a]">{assignment?.title || "Assignment Grades"}</h1> 
                            {assignment?.course?.name && (
                                <p className="text-sm text-[#a17c57]">{assignment.course.name}</p> 
                            )}
                        </div>
                    </div>
                    {submissionData?.submitted && (
                        <div className={`py-2 px-4 rounded-lg flex items-center shadow-sm 
                            ${submissionData?.grade / assignment?.max_score >= 0.8 ? 'bg-green-100' : 
                            submissionData?.grade / assignment?.max_score >= 0.5 ? 'bg-orange-100' : 'bg-red-100'}`}>
                            <span className={`text-${submissionData?.grade / assignment?.max_score >= 0.8 ? 'green' : 
                            submissionData?.grade / assignment?.max_score >= 0.5 ? 'orange' : 'red'}-700 font-semibold mr-2`}>Score:</span>
                            <span
                                className={`text-${submissionData?.grade / assignment?.max_score >= 0.8 ? 'green' : 
                                submissionData?.grade / assignment?.max_score >= 0.5 ? 'orange' : 'red'}-900 font-bold text-lg`}>{submissionData?.grade}/{assignment?.max_score}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* main content */}
            <div className="flex h-[calc(100vh-9rem)] mt-[7rem]">
                {/* student work */}
                <div className="w-1/2 p-4">
                    {!submissionData?.submitted ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 rounded-xl">
                            <img src="/assets/folder.png" alt="Missing Submission" className="w-24 h-24 mb-4"/>
                            <p className="text-xl font-semibold text-gray-700">Missing Submission</p>
                        </div>
                    ) : submissionData?.essay_text ? (
                        <div className="w-full h-full overflow-auto bg-white border border-gray-300 p-4 rounded-xl">
                            <pre className="whitespace-pre-wrap font-sans">{submissionData?.essay_text}</pre>
                        </div>
                    ) : assignment?.type === 'flexible_problem_set' ? (
                        <div
                            className="bg-white h-full border border-gray-200 rounded-lg shadow-sm p-6 overflow-auto mt-[10px]">
                            <SUnpublishedFlexibleAssignment
                                isReadMode={assignment.is_grades_publishede}
                                isPregradeMode={!assignment.is_grades_published}
                                assignment_id={assignment.id}
                                assignment={assignment}
                                class_id={assignment.course_id}
                                studentId={submissionData.user_id}
                                isPublicView={true}
                            />
                        </div>
                    ) : displaySettings.show_pdf ? (
                        <div
                            className="ml-5 mr-5 w-1/2 p-4 h-[calc(100vh-10rem)] min-w-full border border-gray-300 rounded-xl">
                            <div className="w-full h-full relative flex-1">
                                <PDFView
                                    url={`${apiUrl}/api/pdf-file/${submissionData?.file.filename}`}
                                    className="absolute inset-0 flex-1"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 rounded-xl">
                            <p className="text-xl font-semibold text-gray-700">PDF view disabled by instructor</p>
                        </div>
                    )}
                </div>

                {/* feedback section */}
                <div className="w-1/2 overflow-y-auto p-8">
                    <div className="flex flex-col">
                        {/* Warning message for pregrades */}
                        {submissionData?.submitted && !assignment?.is_grades_published &&
                            assignment?.allow_pregrade &&
                            submissionData?.submission_count == 1 && (
                                <div className="bg-yellow-50/80 border-l-4 border-yellow-400 p-4 mb-6">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <img
                                                src="/assets/tclasses/warning.png"
                                                alt="Warning"
                                                className="h-6 w-6 text-yellow-400"
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-base text-yellow-700 font-semibold mb-1">
                                                These grades were produced by GradeWiz AI with NO OVERSIGHT from
                                                professor.
                                            </p>
                                            <p className="text-base text-yellow-700">
                                                Your final grade MAY BE DIFFERENT. Use these grades for your
                                                REFERENCE ONLY.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                        {assignment?.questions?.map((question, questionIndex) => (
                            <div key={questionIndex} className="mb-8">
                                <h2 className="text-xl font-bold mb-2">
                                    {`Question ${questionIndex + 1}:`}
                                </h2>

                                {/* Main question text */}
                                {displaySettings.show_pdf && question.description && (
                                    <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <TruncatedText
                                            text={question.description}
                                            className="text-gray-700 leading-relaxed"
                                            maxLength={70}
                                        />
                                    </div>
                                )}

                                {question.subQuestions.map((subQuestion, subQuestionIndex) => (
                                    <div key={subQuestionIndex} className="mb-6 ml-4">
                                        {assignment.type === 'essay' ? (
                                            <div className="mb-2">
                                                <h3 className="font-semibold mb-1">
                                                    {`${String.fromCharCode(97 + subQuestionIndex)})`}
                                                </h3>
                                                {displaySettings.show_pdf && (
                                                    <TruncatedText
                                                        text={subQuestion.question}
                                                        className="ml-5 text-gray-700"
                                                        maxLength={70}
                                                    />
                                                )}
                                            </div>
                                        ) : assignment.type === 'flexible_problem_set' ? (
                                            !question.is_without_sub_questions && (
                                                <div className="mb-2">
                                                    <h3 className="font-semibold mb-1">
                                                        {`${String.fromCharCode(97 + subQuestionIndex)})`}
                                                    </h3>
                                                    {displaySettings.show_pdf && (
                                                        <TruncatedText
                                                            text={subQuestion.question}
                                                            className="ml-5 text-gray-700"
                                                            maxLength={70}
                                                        />
                                                    )}
                                                </div>
                                            )
                                        ) : (
                                            <>
                                                <h3 className="font-bold mb-1 pt-2">
                                                    {questionIndex + 1}. {String.fromCharCode(97 + subQuestionIndex)})
                                                </h3>
                                                {displaySettings.show_pdf && subQuestion.question && (
                                                    <TruncatedText
                                                        text={subQuestion.question}
                                                        className="ml-5 mb-2 text-gray-700"
                                                        maxLength={70}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {displaySettings.show_grades && (
                                            <div className="flex flex-col gap-2 mb-2 mt-3">
                                                <ScoreScale subQuestion={subQuestion} />
                                            </div>
                                        )}

                                        {displaySettings.show_feedback && getFeedback(subQuestion.id) && (
                                            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-medium text-gray-600 text-sm uppercase tracking-wide flex items-center">
                                                        <LightbulbIcon className="w-5 h-5 mr-2 text-amber-500"/>
                                                        Feedback
                                                    </h4>
                                                </div>
                                                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                    {getFeedback(subQuestion.id)}
                                                </div>
                                            </div>
                                        )}
                                        {displaySettings.show_feedback && getInstructorFeedback(subQuestion.id) && (
                                            <div
                                                className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <h4 className="font-medium text-gray-600 text-sm uppercase tracking-wide mb-2 flex items-center">
                                                    <UserCircleIcon className="w-5 h-5 mr-2 text-gray-400"/>
                                                    Instructor Comments
                                                </h4>
                                                <div className="text-gray-700 leading-relaxed">
                                                    {getInstructorFeedback(subQuestion.id)}
                                                </div>
                                            </div>
                                        )}

                                        {/* Regrade Request UI */}
                                        {displaySettings.show_grades && feedbackItems[subQuestion.id] && (
                                            <div className="mt-4">
                                                {feedbackItems[subQuestion.id]?.regrade_request && !feedbackItems[subQuestion.id]?.edit_mode ? (
                                                    <div className="bg-orange-100 p-4 rounded-xl">
                                                        <h4 className="font-semibold mb-2">
                                                            Regrade
                                                            Request: {feedbackItems[subQuestion.id]?.regrade_text}
                                                        </h4>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                className="mt-1 px-5 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-orange-200 hover:bg-orange-300 text-black"
                                                                type="button"
                                                                onClick={() => handleEditRegradeReq(subQuestion.id)}
                                                            >
                                                                Edit Request
                                                            </button>
                                                            <button
                                                                className="mt-1 px-5 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-white hover:bg-gray-300 text-black"
                                                                type="button"
                                                                onClick={(e) => {
                                                                    // Prevent any default form submission
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleRegradeReqCancel(subQuestion.id);
                                                                }}
                                                            >
                                                                Cancel Request
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : feedbackItems[subQuestion.id]?.edit_mode ? (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            placeholder="Reason for regrade request"
                                                            value={feedbackItems[subQuestion.id]?.regrade_text || ''}
                                                            className="mt-2 border rounded-xl px-4 py-2 mb-2 w-full focus:border-orange-200 focus:ring-gray-400 focus:outline-none"
                                                            onChange={e =>
                                                                setFeedbackItems(prevFeedbackItems => ({
                                                                    ...prevFeedbackItems,
                                                                    [subQuestion.id]: {
                                                                        ...prevFeedbackItems[subQuestion.id],
                                                                        regrade_text: e.target.value
                                                                    }
                                                                }))
                                                            }
                                                        />
                                                        <div className="flex space-x-2">
                                                            <button
                                                                className={`mt-1 px-5 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow ${
                                                                    !(feedbackItems[subQuestion.id]?.regrade_text || '').trim()
                                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-orange-200 hover:bg-orange-300 text-black'
                                                                }`}
                                                                type="button"
                                                                onClick={() => {
                                                                    // Only proceed with save if there's text
                                                                    if ((feedbackItems[subQuestion.id]?.regrade_text || '').trim()) {
                                                                        handleRegradeReqSave(subQuestion.id);
                                                                    }
                                                                }}
                                                            >
                                                                Submit Request
                                                            </button>
                                                            <button
                                                                className="mt-1 px-5 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-white hover:bg-gray-300 text-black"
                                                                type="button"
                                                                onClick={() => {
                                                                    if (feedbackItems[subQuestion.id]?.is_new_request) {
                                                                        // If this is a new request being created, cancel the whole thing
                                                                        handleRegradeReqCancel(subQuestion.id);
                                                                    } else {
                                                                        // If editing an existing request, just exit edit mode
                                                                        handleCancelEdit(subQuestion.id);
                                                                    }
                                                                }}
                                                            >
                                                                {feedbackItems[subQuestion.id]?.is_new_request ? 'Cancel' : 'Cancel Editing'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    assignment.allow_regrade && (
                                                        <button
                                                            className="mt-1 px-5 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-white hover:bg-gray-300 text-black"
                                                            type="button"
                                                            onClick={() => handleRegradeReq(subQuestion.id)}
                                                        >
                                                            Request Regrade
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}

                                    </div>
                                ))}
                            </div>
                        ))}

                        {assignment?.type === 'essay' && submissionData?.submitted && (
                            <div className="w-full bg-white border border-gray-300 p-4 rounded-xl">
                                <style>{`
                                .feedback-content h2 {
                                    font-size: 20px;
                                    font-weight: var(--h2-font-weight);
                                    margin: var(--h2-margin);
                                }
                                .feedback-content p {
                                    margin: var(--p-margin);
                                }
                                .feedback-content ul {
                                    list-style-type: disc;
                                    padding: var(--ul-padding);
                                    margin: var(--ul-margin);
                                }
                                .feedback-content li {
                                    margin: var(--li-margin);
                                }
                            `}</style>
                                <div
                                    dangerouslySetInnerHTML={{__html: submissionData?.file?.filename}}
                                    className="feedback-content"
                                    style={{
                                        '--h2-font-size': '1.5em',
                                        '--h2-font-weight': 'bold',
                                        '--h2-margin': '1em 0 0.5em',
                                        '--p-margin': '0 0 1em',
                                        '--ul-padding': '0 0 0 2em',
                                        '--ul-margin': '0 0 1em',
                                        '--li-margin': '0 0 0.5em',
                                        height: 'auto',
                                        overflow: 'visible'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {message && (
                    <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PublicGradeView;